import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Share } from 'lucide-react';
import { Card } from './ui/card';

const DISMISS_KEY = 'pwa-dismissed-at';
const DISMISS_DAYS = 7;

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    // Dismissed recently?
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_DAYS * 86400000) return;

    // iOS Safari detection
    const ua = navigator.userAgent;
    const iosDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (iosDevice) {
      setIsIOS(true);
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // Chrome/Edge beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2500);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem('pwa-installed', 'true');
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;
  if (!isIOS && !deferredPrompt) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="pwa-install-prompt">
      <Card className="bg-gray-800 border-gray-700 p-5 max-w-md w-full shadow-2xl">
        <div className="flex items-start gap-4">
          <img src="/icon-192.png" alt="ART Garage" className="w-14 h-14 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start mb-1.5">
              <h3 className="text-white font-bold text-base">ART Garage</h3>
              <button onClick={handleDismiss} className="text-gray-500 hover:text-white" data-testid="pwa-dismiss">
                <X className="h-4 w-4" />
              </button>
            </div>
            {isIOS ? (
              <p className="text-gray-400 text-sm leading-relaxed">
                Uygulamayi yuklemek icin alttaki
                <Share className="inline h-4 w-4 mx-1 text-brand" />
                <span className="text-brand font-medium">Paylas</span> butonuna,
                sonra <span className="text-brand font-medium">Ana Ekrana Ekle</span> secenegine dokunun.
              </p>
            ) : (
              <p className="text-gray-400 text-sm">
                Ana ekraniniza ekleyerek hizli erisim saglayin.
              </p>
            )}
            <div className="flex gap-2 mt-3">
              {!isIOS && (
                <Button onClick={handleInstall}
                  className="flex-1 bg-brand hover:bg-brand text-white font-semibold text-sm"
                  data-testid="pwa-install-btn">
                  <Download className="h-4 w-4 mr-1.5" />Yukle
                </Button>
              )}
              <Button onClick={handleDismiss} variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
                data-testid="pwa-later-btn">
                Daha Sonra
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
